/**
 * The server-owned prompt queue. A module of its own, not part of
 * conversations.ts, because Bun module mocks are process-global and 16 test
 * files mock conversations.ts with partial exports. It imports nothing from
 * client.ts for the same reason (see http.ts).
 */
import { DAEMON_URL } from "../config";
import type { OutgoingImage } from "../lib/attachments";
import { doFetch } from "./http";
import type { EnqueueResult, NextStart, QueueItemWire, QueueState } from "./types";

export type { EnqueueResult } from "./types";

/** How long one `awaitQueueStart` call waits. The daemon caps the wait at 30s. */
export const QUEUE_WAIT_MS = 25_000;

/**
 * How long the column waits before asking again after the daemon refused a
 * queue wait with 429 `too_many_waiters` (too many tabs of this user are
 * already waiting). The fake queue in tests sets it to 0.
 */
export const QUEUE_WAITERS_BACKOFF_MS = 3_000;

function queueUrl(project: string, conversationId: number, suffix = ""): string {
  return `${DAEMON_URL}/chat/${encodeURIComponent(project)}/${encodeURIComponent(String(conversationId))}/queue${suffix}`;
}

const JSON_HEADERS = { "Accept": "application/json", "Content-Type": "application/json" };

export async function getQueue(project: string, conversationId: number): Promise<QueueState> {
  const res = await doFetch(queueUrl(project, conversationId), {
    method: "GET",
    headers: { "Accept": "application/json" },
    credentials: "include",
  });
  return (await res.json()) as QueueState;
}

/**
 * Save a prompt in the queue. `clientId` makes a retry idempotent: a live row
 * with that id is answered again, and a prompt that already ran answers with
 * `item: null` and the turn it became (`EnqueueResult`).
 */
export async function enqueuePrompt(
  project: string,
  conversationId: number,
  body: { message: string; images: OutgoingImage[]; clientId: string },
): Promise<EnqueueResult> {
  const res = await doFetch(queueUrl(project, conversationId), {
    method: "POST",
    headers: JSON_HEADERS,
    credentials: "include",
    body: JSON.stringify(body),
  });
  return (await res.json()) as EnqueueResult;
}

export async function editQueued(
  project: string,
  conversationId: number,
  itemId: number,
  body: { message: string; keep: string[]; images: OutgoingImage[] },
): Promise<QueueItemWire> {
  const res = await doFetch(queueUrl(project, conversationId, `/${itemId}`), {
    method: "PATCH",
    headers: JSON_HEADERS,
    credentials: "include",
    body: JSON.stringify(body),
  });
  return ((await res.json()) as { item: QueueItemWire }).item;
}

export async function moveQueuedItem(
  project: string,
  conversationId: number,
  itemId: number,
  toIndex: number,
): Promise<QueueItemWire[]> {
  const res = await doFetch(queueUrl(project, conversationId, `/${itemId}/move`), {
    method: "POST",
    headers: JSON_HEADERS,
    credentials: "include",
    body: JSON.stringify({ toIndex }),
  });
  return ((await res.json()) as { items: QueueItemWire[] }).items;
}

export async function deleteQueued(project: string, conversationId: number, itemId: number): Promise<void> {
  await doFetch(queueUrl(project, conversationId, `/${itemId}`), {
    method: "DELETE",
    headers: { "Accept": "application/json" },
    credentials: "include",
  });
}

export async function clearQueued(project: string, conversationId: number): Promise<void> {
  await doFetch(queueUrl(project, conversationId), {
    method: "DELETE",
    headers: { "Accept": "application/json" },
    credentials: "include",
  });
}

export async function resumeQueue(
  project: string,
  conversationId: number,
): Promise<{ started: boolean; nextItemId: number | null }> {
  const res = await doFetch(queueUrl(project, conversationId, "/resume"), {
    method: "POST",
    headers: JSON_HEADERS,
    credentials: "include",
    body: JSON.stringify({}),
  });
  return (await res.json()) as { started: boolean; nextItemId: number | null };
}

/**
 * Long-poll until a reserved queue item is a running turn. With `itemId` the
 * daemon answers only for the turn made from that item (the `nextItemId` of a
 * `done` frame or of an enqueue/resume answer), never for some other streaming
 * turn. `signal` aborts the wait; the abort surfaces as an AbortError.
 */
export async function awaitQueueStart(
  project: string,
  conversationId: number,
  timeoutMs: number,
  itemId?: number,
  signal?: AbortSignal,
): Promise<NextStart> {
  const query = `?timeoutMs=${timeoutMs}${itemId !== undefined ? `&itemId=${itemId}` : ""}`;
  const res = await doFetch(`${queueUrl(project, conversationId, "/next")}${query}`, {
    method: "GET",
    headers: { "Accept": "application/json" },
    credentials: "include",
    ...(signal ? { signal } : {}),
  });
  return (await res.json()) as NextStart;
}
